import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import Plot from "react-plotly.js";
import {
  fetchSkuDetails,
  fetchSkuConstraints,
  fetchSkuForecast,
  fetchSkuWinrate,
  downloadSkuForecast,
  downloadSkuWinrate,
  fetchConfig,
} from "./api";
import { getKpiChartConfig, getForecastChartConfig, getWinrateChartConfig, aggregateByWeek, aggregateWinrateByWeek, formatPeriod } from "./chartData";
import { useAlertsCount, useAlertsLoading } from "./AlertsContext";
import InfoIcon, { SortableHeader } from "./InfoIcon";
import "./App.css";

const iconVisible = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="#f5f5f5"
    viewBox="0 0 256 256"
  >
    <path d="M247.31,124.76c-.35-.79-8.82-19.58-27.65-38.41C194.57,61.26,162.88,48,128,48S61.43,61.26,36.34,86.35C17.51,105.18,9,124,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208s66.57-13.26,91.66-38.34c18.83-18.83,27.3-37.61,27.65-38.4A8,8,0,0,0,247.31,124.76ZM128,168a40,40,0,1,1,40-40A40,40,0,0,1,128,168Z"></path>
  </svg>
);

const iconHidden = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="#f5f5f5"
    viewBox="0 0 256 256"
  >
    <path d="M96.68,57.87a4,4,0,0,1,2.08-6.6A130.13,130.13,0,0,1,128,48c34.88,0,66.57,13.26,91.66,38.35,18.83,18.83,27.3,37.62,27.65,38.41a8,8,0,0,1,0,6.5c-.35.79-8.82,19.57-27.65,38.4q-4.28,4.26-8.79,8.07a4,4,0,0,1-5.55-.36ZM213.92,210.62a8,8,0,1,1-11.84,10.76L180,197.13A127.21,127.21,0,0,1,128,208c-34.88,0-66.57-13.26-91.66-38.34C17.51,150.83,9,132.05,8.69,131.26a8,8,0,0,1,0-6.5C9,124,17.51,105.18,36.34,86.35a135,135,0,0,1,25-19.78L42.08,45.38A8,8,0,1,1,53.92,34.62Zm-65.49-48.25-52.69-58a40,40,0,0,0,52.69,58Z"></path>
  </svg>
);

const iconDownload = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="#ffffff"
    viewBox="0 0 256 256"
  >
    <path d="M222,144v64a6,6,0,0,1-6,6H40a6,6,0,0,1-6-6V144a6,6,0,0,1,12,0v58H210V144a6,6,0,0,1,12,0Zm-98.24,4.24a6,6,0,0,0,8.48,0l40-40a6,6,0,0,0-8.48-8.48L134,129.51V32a6,6,0,0,0-12,0v97.51L92.24,99.76a6,6,0,0,0-8.48,8.48Z"></path>
  </svg>
);

const forecastColumns = [
  "MONTH",
  "ACT QTY Y-1",
  "FCST QTY - σ",
  "FCST QTY (avg)",
  "FCST QTY + σ",
  "ACT VAL Y-1",
  "FCST VALUE - σ",
  "FCST VALUE (avg)",
  "FCST VALUE + σ",
];

const forecastMap = {
  MONTH: "year_week",
  "ACT QTY Y-1": "actual_qty_last_year",
  "FCST QTY - σ": "fcst_qty_lowerbound",
  "FCST QTY (avg)": "fcst_qty_avg",
  "FCST QTY + σ": "fcst_qty_upperbound",
  "ACT VAL Y-1": "actual_val_last_year",
  "FCST VALUE - σ": "fcst_val_lowerbound",
  "FCST VALUE (avg)": "fcst_val_avg",
  "FCST VALUE + σ": "fcst_val_upperbound",
};

const winrateColumns = [
  "MONTH",
  "ACT WR Y-1 RAW",
  "FCST WR (avg) RAW",
  "ACT WR Y-1 SHRINKED",
  "FCST WR (avg) SHRINKED",
];

const winrateMap = {
  MONTH: "year_week",
  "ACT WR Y-1 RAW": "actual_wr_last_year",
  "FCST WR (avg) RAW": "fcst_qty_avg",
  "ACT WR Y-1 SHRINKED": "actual_wr_last_year_shrinked",
  "FCST WR (avg) SHRINKED": "fcst_qty_avg_shrinked",
};

// QUESTO OGGETTO DI CONFIGURAZIONE MAPPA I DATI DELL'API PER LA VISUALIZZAZIONE
//    È la chiave per mostrare dinamicamente tutti i dati ricevuti.
const skuConstraintConfig = {
  min_roi: { label: "Minimum ROI", type: "percentage" },
  moq: { label: "MOQ", type: "number" },
  max_num_of_pos_per_year: { label: "Max Num of POs Per Year", type: "number" },
};

// Configurazione per rendere la tabella dinamica e riordinabile
const FORECAST_COLUMN_DEFS = {
  MONTH: {
    render: (row) => formatPeriod(row.year_week),
  },
  "ACT QTY Y-1": {
    render: (row, formatNumber) => formatNumber(row.actual_qty_last_year),
  },
  "FCST QTY - σ": {
    className: "fcst-minus",
    render: (row, formatNumber) =>
      formatNumber(Math.round(row.fcst_qty_lowerbound)),
  },
  "FCST QTY (avg)": {
    className: "fcst-avg",
    render: (row, formatNumber) => formatNumber(Math.round(row.fcst_qty_avg)),
  },
  "FCST QTY + σ": {
    className: "fcst-plus",
    render: (row, formatNumber) =>
      formatNumber(Math.round(row.fcst_qty_upperbound)),
  },
  "ACT VAL Y-1": {
    render: (row, _, formatCurrency) =>
      formatCurrency(row.actual_val_last_year),
  },
  "FCST VALUE - σ": {
    className: "fcst-minus",
    render: (row, _, formatCurrency) => formatCurrency(row.fcst_val_lowerbound),
  },
  "FCST VALUE (avg)": {
    className: "fcst-avg",
    render: (row, _, formatCurrency) => formatCurrency(row.fcst_val_avg),
  },
  "FCST VALUE + σ": {
    className: "fcst-plus",
    render: (row, _, formatCurrency) => formatCurrency(row.fcst_val_upperbound),
  },
};

// Hook per il salvataggio in LocalStorage
function useStickyState(defaultValue, key) {
  const [value, setValue] = useState(() => {
    try {
      const stickyValue = window.localStorage.getItem(key);
      return stickyValue !== null ? JSON.parse(stickyValue) : defaultValue;
    } catch (e) {
      console.warn("Error reading from localStorage", e);
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.warn("Error writing to localStorage", e);
    }
  }, [key, value]);

  return [value, setValue];
}

function SkuView() {
  const alertsCount = useAlertsCount();
  const alertsLoading = useAlertsLoading();
  const { skuId } = useParams(); // Prende l'ID SKU dall'URL
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");
  const [loading, setLoading] = useState(true);
  const [popupChart, setPopupChart] = useState(null);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);

  const [skuDetails, setSkuDetails] = useState(null);
  const [skuKpis, setSkuKpis] = useState(null);
  const [skuKpiHistory, setSkuKpiHistory] = useState(null);
  const [unconstrainedSkuKpiHistory, setUnconstrainedSkuKpiHistory] = useState(null);
  const [skuConstraints, setSkuConstraints] = useState(null);
  const [forecastData, setForecastData] = useState([]);
  const [allForecastData, setAllForecastData] = useState([]);
  const [availableRuns, setAvailableRuns] = useState([]);
  const [selectedGenerationIndex, setSelectedGenerationIndex] = useState(0);
  const [forecastMode, setForecastMode] = useState("demand");
  const [customerType, setCustomerType] = useState("combined");
  const [availabilityType, setAvailabilityType] = useState("available");
  const [winrateData, setWinrateData] = useState([]);
  const [allWinrateData, setAllWinrateData] = useState([]);
  const [winrateRuns, setWinrateRuns] = useState([]);
  const [selectedWinrateGenIndex, setSelectedWinrateGenIndex] = useState(0);
  const [isCompareActive, setIsCompareActive] = useState(false);
  const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);
  const [isDownloadingForecast, setIsDownloadingForecast] = useState(false);
  const [isDownloadingWinrate, setIsDownloadingWinrate] = useState(false);
  const forecastColsRef = useRef(null);
  const [forecastSortConfig, setForecastSortConfig] = useState({
    key: null,
    direction: null,
  });

  // Ordine Colonne (Salvato in LocalStorage)
  const [forecastColumnOrder, setForecastColumnOrder] = useStickyState(
    forecastColumns,
    "skuView_forecastOrder_v2"
  );

  // Visibilità Colonne (Salvato in LocalStorage)
  const [columnVisibility, setColumnVisibility] = useStickyState(
    forecastColumns.reduce((acc, col) => ({ ...acc, [col]: true }), {}),
    "skuView_forecastVisibility_v2"
  );

  const [winrateColumnVisibility, setWinrateColumnVisibility] = useStickyState(
    winrateColumns.reduce((acc, col) => ({ ...acc, [col]: true }), {}),
    "skuView_winrateVisibility_v3"
  );

  const filteredForecastData = useMemo(
    () => aggregateByWeek(forecastData, customerType),
    [forecastData, customerType]
  );
  const filteredWinrateData = useMemo(
    () => aggregateWinrateByWeek(winrateData, forecastData, customerType),
    [winrateData, forecastData, customerType]
  );

  const [isWinrateCompareActive, setIsWinrateCompareActive] = useState(false);
  const [oppositeWinrateData, setOppositeWinrateData] = useState([]);

  useEffect(() => {
    setOppositeWinrateData([]);
    if (!isWinrateCompareActive || !skuId) return undefined;
    let cancelled = false;
    fetchSkuWinrate(skuId, {
      customer_type: customerType,
      availability: availabilityType === "available" ? "unavailable" : "available",
    })
      .then((res) => {
        if (!cancelled) setOppositeWinrateData(res?.forecast_result || []);
      })
      .catch((err) => console.error("Failed to fetch comparison win rate:", err));
    return () => {
      cancelled = true;
    };
  }, [isWinrateCompareActive, skuId, customerType, availabilityType]);

  const winrateCompareMap = useMemo(() => {
    if (!isWinrateCompareActive) return null;
    const runId = winrateRuns[selectedWinrateGenIndex]?.run_id;
    const rows = oppositeWinrateData.filter((r) => r.run_id === runId);
    return new Map(
      aggregateWinrateByWeek(rows, forecastData, customerType).map((r) => [r.year_week, r]),
    );
  }, [isWinrateCompareActive, oppositeWinrateData, winrateRuns, selectedWinrateGenIndex, forecastData, customerType]);

  useEffect(() => {
    const reconcile = (setter, columns) =>
      setter((prev) => {
        const next = {};
        let changed = Object.keys(prev).some((key) => !columns.includes(key));
        columns.forEach((col) => {
          if (prev[col] === undefined) changed = true;
          next[col] = prev[col] === undefined ? true : prev[col];
        });
        return changed ? next : prev;
      });
    reconcile(setColumnVisibility, forecastColumns);
    reconcile(setWinrateColumnVisibility, winrateColumns);
    setForecastColumnOrder((prev) => {
      const kept = prev.filter((col) => forecastColumns.includes(col));
      const missing = forecastColumns.filter((col) => !kept.includes(col));
      return kept.length !== prev.length || missing.length > 0
        ? [...kept, ...missing]
        : prev;
    });
  }, [setColumnVisibility, setWinrateColumnVisibility, setForecastColumnOrder]);

  const showWrCol = (col) => winrateColumnVisibility[col];

  const renderWinrateCell = (row, field) => {
    const format = (v) => (v != null ? (v * 100).toFixed(1) + "%" : "—");
    const base = row[field];
    const target = isWinrateCompareActive ? winrateCompareMap?.get(row.year_week)?.[field] : null;
    if (base == null || target == null) return format(base);
    const delta = target - base;
    const deltaClass =
      delta === 0 ? "compare-neutral" : delta > 0 ? "compare-improvement" : "compare-degradation";
    const deltaLabel =
      delta === 0
        ? "0%"
        : base !== 0
          ? `${delta > 0 ? "+" : ""}${((delta / Math.abs(base)) * 100).toFixed(1)}%`
          : "—";
    return (
      <span
        className="compare-cell-content"
        title={
          availabilityType === "available"
            ? `Available ${format(base)} → Unavailable ${format(target)} (${deltaLabel})`
            : `Unavailable ${format(base)} → Available ${format(target)} (${deltaLabel})`
        }
      >
        <span className="compare-target">{format(base)}</span>
        <span className={`compare-delta ${deltaClass}`}>{deltaLabel}</span>
      </span>
    );
  };

  const comparisonData = useMemo(() => {
    if (!isCompareActive) return null;
    if (forecastMode === "demand") {
      if (availableRuns.length === 0) return null;
      const currentRunId = availableRuns[0].run_id;
      return aggregateByWeek(allForecastData.filter((row) => row.run_id === currentRunId), customerType);
    } else {
      if (winrateRuns.length === 0) return null;
      const currentRunId = winrateRuns[0].run_id;
      const compDemand = availableRuns.length > 0
        ? allForecastData.filter((row) => row.run_id === availableRuns[0].run_id)
        : forecastData;
      return aggregateWinrateByWeek(allWinrateData.filter((row) => row.run_id === currentRunId), compDemand, customerType);
    }
  }, [isCompareActive, forecastMode, availableRuns, allForecastData, winrateRuns, allWinrateData, customerType, forecastData]);

  const chartConstraints = useMemo(() => {
    return { ...skuConstraints, min_service_level: null };
  }, [skuConstraints]);

  const processForecastData = (rawData) => {
    if (!rawData || rawData.length === 0) return { runs: [], sortedData: [] };

    const runMap = new Map();

    rawData.forEach((row) => {
      if (!runMap.has(row.run_id)) {
        runMap.set(row.run_id, {
          run_id: row.run_id,
          run_date: row.run_date,
          parsedDate: new Date(row.run_date),
        });
      }
    });

    const sortedRuns = Array.from(runMap.values()).sort((a, b) => {
      return b.parsedDate - a.parsedDate;
    });

    return { runs: sortedRuns, sortedData: rawData };
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      setSkuDetails(null);
      setSkuKpis(null);
      setSkuKpiHistory(null);

      try {
        // Carica configurazione
        const configData = await fetchConfig();
        setConfig(configData);

        // Esegui le chiamate API in parallelo.
        const [detailsResult, constraintsResult] =
          await Promise.allSettled([
            fetchSkuDetails(skuId),
            fetchSkuConstraints(skuId),
          ]);

        // --- ELABORAZIONE DETTAGLI E STORICO (SKU DETAILS) ---
        if (
          detailsResult.status === "fulfilled" &&
          detailsResult.value?.sku_details
        ) {
          const rawList = detailsResult.value.sku_details;

          const prepareSkuHistory = (items) =>
            items
              .filter((item) => item.run_date)
              .map((item) => ({ ...item, parsedDate: new Date(item.run_date) }))
              .sort((a, b) => a.parsedDate - b.parsedDate);

          const constrainedData = prepareSkuHistory(
            rawList.filter((item) => item.optimization_mode === "constrained"),
          );
          const unconstrainedData = prepareSkuHistory(
            rawList.filter((item) => item.optimization_mode === "unconstrained"),
          );

          const buildSkuHistory = (sortedData) => ({
            lq: sortedData.map((d) => d.replenishment_policy.lq),
            roq: sortedData.map((d) => d.replenishment_policy.rq),
            roi: sortedData.map((d) => d.expected_value.roi.current),
            sl: sortedData.map((d) => d.expected_value.service_level.current),
            cost: sortedData.map((d) => d.expected_value.cost),
            revenues: sortedData.map((d) => d.expected_value.revenues.current),
            netMargin: sortedData.map((d) => d.expected_value.net_margin),
            grossMargin: sortedData.map((d) => d.expected_value.gross_margin),
            runLabels: sortedData.map((d) => {
              try {
                return d.parsedDate.toISOString().split("T")[0];
              } catch (e) {
                return d.run_id;
              }
            }),
            rawData: sortedData,
          });

          if (constrainedData.length > 0) {
            const currentData = constrainedData[constrainedData.length - 1];

            setSkuDetails({
              skuId: currentData.sku_id,
              family: currentData.family,
              description: currentData.sku_description,
              leadTime: currentData.lead_time,
              unitPrice: currentData.unit_price,
              unitCost: currentData.unit_cost,
            });

            setSkuKpis({
              lq: currentData.replenishment_policy.lq,
              roq: currentData.replenishment_policy.rq,
              roi: {
                current: currentData.expected_value.roi.current,
                min: currentData.expected_value.roi.min_value,
              },
              sl: {
                current: currentData.expected_value.service_level.current,
                min: currentData.expected_value.service_level.min_value,
              },
              cost: currentData.expected_value.cost,
              revenues: {
                current: currentData.expected_value.revenues.current,
                min: currentData.expected_value.revenues.min_value,
              },
            });

            setSkuKpiHistory(buildSkuHistory(constrainedData));
          } else {
            console.warn("Nessun dato storico valido trovato in sku_details");
          }

          if (unconstrainedData.length > 0) {
            setUnconstrainedSkuKpiHistory(buildSkuHistory(unconstrainedData));
          } else {
            setUnconstrainedSkuKpiHistory(null);
          }
        } else {
          console.warn(
            "Impossibile recuperare i dettagli dello SKU (API fallita o dati mancanti)."
          );
        }

        // --- ELABORAZIONE CONSTRAINTS ---
        const constraintsData =
          constraintsResult.status === "fulfilled" &&
          constraintsResult.value?.sku_constraints?.length
            ? constraintsResult.value.sku_constraints[0]
            : {};
        setSkuConstraints(constraintsData);

      } catch (err) {
        console.error("Errore caricamento dati SKU:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [skuId]);

  const [isForecastLoading, setIsForecastLoading] = useState(false);
  useEffect(() => {
    if (!skuId) return;
    const loadForecastAndWinrate = async () => {
      setIsForecastLoading(true);
      const [fResult, wResult] = await Promise.allSettled([
        fetchSkuForecast(skuId, { customer_type: customerType }),
        fetchSkuWinrate(skuId, { customer_type: customerType, availability: availabilityType }),
      ]);

      if (fResult.status === "fulfilled" && fResult.value?.forecast_result) {
        const rawRows = fResult.value.forecast_result;
        const { runs } = processForecastData(rawRows);
        setAllForecastData(rawRows);
        setAvailableRuns(runs);
        if (runs.length > 0) {
          const currentRunId = runs[0].run_id;
          setForecastData(rawRows.filter((r) => r.run_id === currentRunId));
          setSelectedGenerationIndex(0);
        } else {
          setForecastData([]);
        }
      } else {
        setForecastData([]);
        setAllForecastData([]);
        setAvailableRuns([]);
      }

      if (wResult.status === "fulfilled" && wResult.value?.forecast_result) {
        const rawRows = wResult.value.forecast_result;
        const { runs } = processForecastData(rawRows);
        setAllWinrateData(rawRows);
        setWinrateRuns(runs);
        if (runs.length > 0) {
          setWinrateData(rawRows.filter((r) => r.run_id === runs[0].run_id));
          setSelectedWinrateGenIndex(0);
        } else {
          setWinrateData([]);
        }
      } else {
        setWinrateData([]);
        setAllWinrateData([]);
        setWinrateRuns([]);
      }
      setIsForecastLoading(false);
    };
    loadForecastAndWinrate();
  }, [skuId, customerType, availabilityType]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        forecastColsRef.current &&
        !forecastColsRef.current.contains(event.target)
      ) {
        setIsColumnDropdownOpen(false);
      }
      if (!event.target.closest(".split-btn-container")) {
        setIsDownloadDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDownloadForecast = async () => {
    // Verifica che ci sia una run selezionata
    if (!availableRuns[selectedGenerationIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = availableRuns[selectedGenerationIndex];
    const currentRunId = currentRun.run_id;

    // Nome file pulito
    const safeSkuId = skuDetails?.skuId || skuId;
    const filename = `forecast_${safeSkuId}_${currentRunId}_${customerType}.xlsx`;

    setIsDownloadingForecast(true);
    try {
      await downloadSkuForecast(safeSkuId, { run_id: currentRunId, customer_type: customerType }, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading forecast file.");
    } finally {
      setIsDownloadingForecast(false);
    }
  };

  const handleDownloadForecastUnfiltered = async () => {
    setIsDownloadDropdownOpen(false);
    if (!availableRuns[selectedGenerationIndex]) {
      alert("No generation selected to download.");
      return;
    }
    const currentRunId = availableRuns[selectedGenerationIndex].run_id;
    const safeSkuId = skuId.replace(/[^a-z0-9]/gi, "_");
    setIsDownloadingForecast(true);
    try {
      await downloadSkuForecast(skuId, { run_id: currentRunId }, `forecast_${safeSkuId}_${currentRunId}_unfiltered.xlsx`);
    } catch (error) {
      console.error(error);
      alert("Error downloading forecast file.");
    } finally {
      setIsDownloadingForecast(false);
    }
  };

  const handleDownloadWinrateUnfiltered = async () => {
    setIsDownloadDropdownOpen(false);
    if (!winrateRuns[selectedWinrateGenIndex]) {
      alert("No generation selected to download.");
      return;
    }
    const currentRunId = winrateRuns[selectedWinrateGenIndex].run_id;
    const safeSkuId = skuId.replace(/[^a-z0-9]/gi, "_");
    setIsDownloadingWinrate(true);
    try {
      await downloadSkuWinrate(skuId, { run_id: currentRunId, availability: "all" }, `winrate_${safeSkuId}_${currentRunId}_unfiltered.xlsx`);
    } catch (error) {
      console.error(error);
      alert("Error downloading win rate file.");
    } finally {
      setIsDownloadingWinrate(false);
    }
  };

  const handleDownloadWinrate = async () => {
    if (!winrateRuns[selectedWinrateGenIndex]) {
      alert("No generation selected to download.");
      return;
    }

    const currentRun = winrateRuns[selectedWinrateGenIndex];
    const currentRunId = currentRun.run_id;
    const safeSkuId = skuDetails?.skuId || skuId;
    const filename = `winrate_${safeSkuId}_${currentRunId}_${customerType}_${availabilityType}.xlsx`;

    setIsDownloadingWinrate(true);
    try {
      await downloadSkuWinrate(safeSkuId, { run_id: currentRunId, customer_type: customerType, availability: availabilityType }, filename);
    } catch (error) {
      console.error(error);
      alert("Error downloading win rate file.");
    } finally {
      setIsDownloadingWinrate(false);
    }
  };

  const sortedForecastData = useMemo(() => {
    // Filtriamo prima i dati
    let processedData = [...filteredForecastData].filter((row) => {
      return (
        (row.fcst_qty_avg != null && row.fcst_qty_avg !== 0) ||
        (row.fcst_qty_lowerbound != null && row.fcst_qty_lowerbound !== 0) ||
        (row.fcst_qty_upperbound != null && row.fcst_qty_upperbound !== 0) ||
        (row.fcst_val_avg != null && row.fcst_val_avg !== 0) ||
        (row.fcst_val_lowerbound != null && row.fcst_val_lowerbound !== 0) ||
        (row.fcst_val_upperbound != null && row.fcst_val_upperbound !== 0) ||
        (row.actual_qty_last_year != null && row.actual_qty_last_year !== 0) ||
        (row.actual_val_last_year != null && row.actual_val_last_year !== 0)
      );
    });

    // Gestione Ordinamento
    if (forecastSortConfig.key && forecastSortConfig.direction) {
      // Recuperiamo la chiave reale dal mapping
      const dataKey = forecastMap[forecastSortConfig.key];

      processedData.sort((a, b) => {
        let aValue = a[dataKey];
        let bValue = b[dataKey];

        // Gestione valori nulli/undefined
        if (aValue == null)
          aValue = typeof bValue === "string" ? "" : -Infinity;
        if (bValue == null)
          bValue = typeof aValue === "string" ? "" : -Infinity;

        if (aValue < bValue)
          return forecastSortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue)
          return forecastSortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    } else {
      // Ordinamento di Default (per Data/Week crescente) se nessun sort è attivo
      processedData.sort((a, b) => a.year_week.localeCompare(b.year_week));
    }

    return processedData;
  }, [filteredForecastData, forecastSortConfig]);

  const sortedWinrateData = useMemo(() => {
    let processedData = [...filteredWinrateData].filter(
      (row) => (row.fcst_qty_avg != null && row.fcst_qty_avg !== 0) || (row.actual_wr_last_year != null && row.actual_wr_last_year !== 0)
    );
    if (forecastSortConfig.key && forecastSortConfig.direction) {
      const dataKey = winrateMap[forecastSortConfig.key];
      if (dataKey) {
        processedData.sort((a, b) => {
          let aValue = a[dataKey];
          let bValue = b[dataKey];
          if (aValue == null) aValue = -Infinity;
          if (bValue == null) bValue = -Infinity;
          if (aValue < bValue) return forecastSortConfig.direction === "asc" ? -1 : 1;
          if (aValue > bValue) return forecastSortConfig.direction === "asc" ? 1 : -1;
          return 0;
        });
      }
    } else {
      processedData.sort((a, b) => a.year_week.localeCompare(b.year_week));
    }
    return processedData;
  }, [filteredWinrateData, forecastSortConfig]);

  // Funzione Trigger Click
  const requestForecastSort = (colName) => {
    let direction = "asc";
    if (
      forecastSortConfig.key === colName &&
      forecastSortConfig.direction === "asc"
    ) {
      direction = "desc";
    } else if (
      forecastSortConfig.key === colName &&
      forecastSortConfig.direction === "desc"
    ) {
      direction = null; // Reset
    }
    setForecastSortConfig({ key: direction ? colName : null, direction });
  };

  // Indicatore Freccia
  const getForecastSortIndicator = (colName) => {
    if (forecastSortConfig.key !== colName) return null;
    return forecastSortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  const formatCurrency = (num, decimals = 0) => {
    // Se il valore è specificamente null o undefined, mostra N/E
    if (num === null || num === undefined) return "N/A";

    // Converte in numero (gestisce caso stringa "0")
    const val = Number(num);

    // Se non è un numero valido (es. stringa vuota o testo), mostra N/E
    if (isNaN(val)) return "N/A";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(val);
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined) return "N/A";

    const val = Number(num);
    if (isNaN(val)) return "N/A";

    return `${val.toFixed(2)}%`;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "N/A";

    const val = Number(num);
    if (isNaN(val)) return "N/A";

    return val.toLocaleString("en-US");
  };
  const formatConstraintValue = (value, type) => {
    if (value === null || typeof value === "undefined") return "N/A";
    switch (type) {
      case "currency":
        return formatCurrency(value);
      case "percentage":
        return formatPercentage(value);
      case "number":
        return formatNumber(value);
      default:
        return value.toString();
    }
  };
  const handleChartClick = (e, type, name, title) => {
    if (
      e &&
      e.target &&
      (e.target.closest(".modebar") || e.target.closest(".modebar-btn"))
    ) {
      return;
    }
    setPopupChart({ type, name, title });
  };
  const closePopup = () => setPopupChart(null);

  if (loading)
    return (
      <div className="loading-screen">Loading SKU Details for {skuId}...</div>
    );
  if (error) return <div className="error-screen">Error: {error}</div>;

  // Se i dati non sono ancora pronti dopo il caricamento, mostra un messaggio
  if (!config) return <div className="loading-screen">Preparing config...</div>;

  const kpiDisplayData = skuKpis
    ? {
        lq: {
          title: "LQ",
          value: formatNumber(skuKpis.lq),
        },
        roq: {
          title: "ROQ",
          value: formatNumber(skuKpis.roq),
        },
        roi: {
          title: "ROI",
          value: formatPercentage(skuKpis.roi.current),
          limit: `(Min: ${formatPercentage(skuKpis.roi.min)})`,
          isPositive:
            typeof skuKpis.roi.current === "number" && skuKpis.roi.min != null
              ? skuKpis.roi.current >= skuKpis.roi.min
              : null,
        },
        sl: {
          title: "SERVICE LEVEL",
          value: formatPercentage(skuKpis.sl.current),
        },
        cost: {
          title: "TOTAL COST",
          value: formatCurrency(skuKpis.cost),
        },
        revenues: {
          title: "REVENUES",
          value: formatCurrency(skuKpis.revenues.current),
        },
      }
    : {};

  return (
    <>
      <div className={popupChart ? "blur-background" : ""}>
        <header>
          <h1>SKU VIEW</h1>
          <div className="header-buttons">
            {from === "alerts" && (
              <Link to="/alerts" className="header-btn alerts-btn">
                {alertsLoading ? (
                  <>
                    ALERTS{" "}
                    <span
                      className="alerts-count-skeleton"
                      role="status"
                      aria-label="Loading alerts"
                    />
                  </>
                ) : (
                  `ALERTS (${alertsCount})`
                )}
              </Link>
            )}

            <Link
              to={`/forecast?level=SKU&entity=${encodeURIComponent(
                skuDetails?.skuId || skuId
              )}`}
              className="header-btn"
            >
              FORECAST
            </Link>
            <Link
              to={`/family/${skuDetails?.family || "Unknown"}`}
              className="header-btn"
            >
              FAMILY VIEW
            </Link>
            <Link to="/" className="header-btn">
              GLOBAL VIEW
            </Link>
          </div>
        </header>

        <main className="dashboard-container">
          <div className="sku-main-info">
            <p>
              <strong>SKU ID:</strong> {skuDetails?.skuId || skuId}
            </p>
            <p>
              <strong>SKU Family:</strong> {skuDetails?.family || "-"}
            </p>
            <p>
              <strong>SKU Desc:</strong> {skuDetails?.description || "-"}
            </p>
          </div>

          <section className="bordered-section">
            <h2>Optimal Parameters for Replenishment Policy</h2>
            <div className="kpi-grid-top">
              {config.pageLayouts.sku.topKpiBoxes
                .filter((kpi) => kpi.visible)
                .map(({ id }) => {
                  const kpi = kpiDisplayData[id];
                  if (!kpi) return null; // Salta se i dati per questo KPI non sono pronti
                  const chartConfig = getKpiChartConfig(
                    id,
                    skuKpiHistory,
                    skuConstraints,
                    false,
                    config,
                    unconstrainedSkuKpiHistory,
                  );
                  return (
                    <div className="kpi-box" data-kpi-id={id} key={id}>
                      <div className="kpi-title">
                        <InfoIcon label={kpi.title} />
                      </div>
                      <div className="kpi-value">
                        <span>
                          Current: <strong>{kpi.value}</strong>
                        </span>
                      </div>
                      <div
                        className="graph-placeholder"
                        onClick={(e) =>
                          handleChartClick(
                            e,
                            "kpi",
                            id,
                            `${kpi.title} EVOLUTION`
                          )
                        }
                      >
                        <Plot
                          data={chartConfig.traces}
                          layout={chartConfig.layout}
                          config={chartConfig.config}
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
            <h2>EXPECTED VALUE & HISTORY</h2>
            <div className="kpi-grid-bottom">
              {config.pageLayouts.sku.bottomKpiBoxes
                .filter((kpi) => kpi.visible)
                .map(({ id }) => {
                  const kpi = kpiDisplayData[id];
                  if (!kpi) return null;
                  const chartConfig = getKpiChartConfig(
                    id,
                    skuKpiHistory,
                    chartConstraints,
                    false,
                    config,
                    unconstrainedSkuKpiHistory,
                  );
                  return (
                    <div className="kpi-box" data-kpi-id={id} key={id}>
                      <div className="kpi-title">
                        <InfoIcon label={kpi.title} />
                      </div>
                      <div
                        className={`kpi-value ${
                          kpi.isPositive === true
                            ? "positive-value"
                            : kpi.isPositive === false
                            ? "negative-value"
                            : ""
                        }`}
                      >
                        <span>
                          Current: <strong>{kpi.value}</strong>
                        </span>
                        {kpi.limit && (
                          <span className="kpi-min-value">{kpi.limit}</span>
                        )}
                      </div>
                      <div
                        className="graph-placeholder"
                        onClick={(e) =>
                          handleChartClick(
                            e,
                            "kpi",
                            id,
                            `${kpi.title} EVOLUTION`
                          )
                        }
                      >
                        <Plot
                          data={chartConfig.traces}
                          layout={chartConfig.layout}
                          config={chartConfig.config}
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </section>

          <div className="split-section-container">
            <div className="split-box">
              <h2>SKU CONSTRAINTS</h2>
              <div className="split-box-content">
                {config.pageLayouts.sku.constraintItems
                  .filter((item) => item.visible)
                  .map(({ id }) => {
                    const apiKey = id.replace(
                      /[A-Z]/g,
                      (letter) => `_${letter.toLowerCase()}`
                    );

                    if (
                      skuConstraints.hasOwnProperty(apiKey) &&
                      skuConstraintConfig.hasOwnProperty(apiKey)
                    ) {
                      const { label, type } = skuConstraintConfig[apiKey];
                      const value = skuConstraints[apiKey];
                      return (
                        <div className="split-item" key={id}>
                          <span>
                            <InfoIcon label={label} />
                          </span>
                          <span>{formatConstraintValue(value, type)}</span>
                        </div>
                      );
                    }
                    return null;
                  })}
              </div>
            </div>

            <div className="split-box">
              <h2>SKU METRICS</h2>
              <div className="split-box-content">
                {skuDetails ? (
                  <>
                    <div className="split-item">
                      <span>
                        <InfoIcon label="Lead Time" />
                      </span>
                      <span>
                        {skuDetails.leadTime != null
                          ? Math.round(
                              Number(skuDetails.leadTime)
                            ).toLocaleString("en-US")
                          : "-"}{" "}
                        days
                      </span>
                    </div>

                    <div className="split-item">
                      <span>
                        <InfoIcon label="Unit Price" />
                      </span>
                      <span>{formatCurrency(skuDetails.unitPrice, 2)}</span>
                    </div>

                    <div className="split-item">
                      <span>
                        <InfoIcon label="Unit Cost" />
                      </span>
                      <span>{formatCurrency(skuDetails.unitCost, 2)}</span>
                    </div>
                  </>
                ) : (
                  <div
                    className="split-item"
                    style={{ justifyContent: "center" }}
                  >
                    Loading info...
                  </div>
                )}
              </div>
            </div>
          </div>

          <section className="forecast-section">
            <h2>FORECAST</h2>
            <div className="forecast-mode-tabs">
              <button className={`forecast-mode-tab ${forecastMode === "demand" ? "active" : ""}`}
                onClick={() => { setForecastMode("demand"); setIsCompareActive(false); setForecastSortConfig({ key: null, direction: null }); }}>
                Demand
              </button>
              <button className={`forecast-mode-tab ${forecastMode === "winrate" ? "active" : ""}`}
                onClick={() => { setForecastMode("winrate"); setIsCompareActive(false); setForecastSortConfig({ key: null, direction: null }); }}>
                Win Rate
              </button>
            </div>
            <div className="filter-section forecast-controls">
              <span>Generation</span>
              <select id="generation-filter"
                value={forecastMode === "demand" ? selectedGenerationIndex : selectedWinrateGenIndex}
                onChange={(e) => {
                  const newIndex = parseInt(e.target.value, 10);
                  if (forecastMode === "demand") {
                    setSelectedGenerationIndex(newIndex);
                    if (newIndex === 0) setIsCompareActive(false);
                    if (availableRuns[newIndex]) {
                      setForecastData(allForecastData.filter((row) => row.run_id === availableRuns[newIndex].run_id));
                    } else { setForecastData([]); }
                  } else {
                    setSelectedWinrateGenIndex(newIndex);
                    if (newIndex === 0) setIsCompareActive(false);
                    if (winrateRuns[newIndex]) {
                      setWinrateData(allWinrateData.filter((row) => row.run_id === winrateRuns[newIndex].run_id));
                    } else { setWinrateData([]); }
                  }
                }}>
                {(forecastMode === "demand" ? availableRuns : winrateRuns).map((run, index) => {
                  const dateObj = new Date(run.run_date);
                  const label = `${formatPeriod(dateObj.toISOString().slice(0, 10))}, ${dateObj.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                    timeZone: "UTC",
                  })}`;
                  return (<option key={run.run_id} value={index}>{label}</option>);
                })}
                {(forecastMode === "demand" ? availableRuns : winrateRuns).length === 0 && (
                  <option value={0}>No Data</option>
                )}
              </select>

              <span>Customer</span>
              <select
                value={customerType}
                onChange={(e) => setCustomerType(e.target.value)}
              >
                <option value="combined">Combined</option>
                <option value="prime">Prime</option>
                <option value="reseller">Reseller</option>
              </select>

              {forecastMode === "winrate" && (
                <>
                  <span>Type</span>
                  <div className="availability-group">
                    <select
                      value={availabilityType}
                      onChange={(e) => setAvailabilityType(e.target.value)}
                    >
                      <option value="available">Available</option>
                      <option value="unavailable">Unavailable</option>
                    </select>
                    <label
                      className="compare-switch"
                      title={
                        availabilityType === "available"
                          ? "Compare Available → Unavailable"
                          : "Compare Unavailable → Available"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={isWinrateCompareActive}
                        onChange={() => setIsWinrateCompareActive((prev) => !prev)}
                      />
                      <span className="compare-slider" />
                      <span className="compare-switch-label">
                        vs {availabilityType === "available" ? "Unavailable" : "Available"}
                      </span>
                    </label>
                  </div>
                </>
              )}

              <div className="compare-btn-wrapper">
                <button id="compare-toggle-btn"
                  className={`toggle-btn ${isCompareActive ? "active" : ""}`}
                  disabled={forecastMode === "demand" ? selectedGenerationIndex === 0 : selectedWinrateGenIndex === 0}
                  onClick={() => setIsCompareActive(!isCompareActive)}>
                  Compare to Current
                </button>
              </div>

              <div className="toggle-container">
                {forecastMode === "demand" ? (
                  <div className="split-btn-container" style={{ marginRight: "10px", marginLeft: "10px" }}>
                    <button
                      onClick={handleDownloadForecast}
                      className={`split-main-btn ${isDownloadingForecast ? "loading" : ""}`}
                      disabled={isDownloadingForecast}
                      title="Download XLSX"
                    >
                      <div className="btn-icon">{iconDownload}</div>
                      <span>Download</span>
                    </button>
                    <button
                      className="split-arrow-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDownloadDropdownOpen(!isDownloadDropdownOpen);
                      }}
                    >
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                        style={{ transform: isDownloadDropdownOpen ? "rotate(-180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    {isDownloadDropdownOpen && (
                      <div className="split-dropdown-menu">
                        <button
                          onClick={handleDownloadForecastUnfiltered}
                          className={`split-dropdown-item ${isDownloadingForecast ? "loading" : ""}`}
                          disabled={isDownloadingForecast || availableRuns.length === 0}
                        >
                          <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                          <span>Download without filters</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="split-btn-container" style={{ marginRight: "10px", marginLeft: "10px" }}>
                    <button
                      onClick={handleDownloadWinrate}
                      className={`split-main-btn ${isDownloadingWinrate ? "loading" : ""}`}
                      disabled={isDownloadingWinrate}
                      title="Download Win Rate"
                    >
                      <div className="btn-icon">{iconDownload}</div>
                      <span>Download</span>
                    </button>
                    <button
                      className="split-arrow-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDownloadDropdownOpen(!isDownloadDropdownOpen);
                      }}
                    >
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"
                        style={{ transform: isDownloadDropdownOpen ? "rotate(-180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    {isDownloadDropdownOpen && (
                      <div className="split-dropdown-menu">
                        <button
                          onClick={handleDownloadWinrateUnfiltered}
                          className={`split-dropdown-item ${isDownloadingWinrate ? "loading" : ""}`}
                          disabled={isDownloadingWinrate || winrateRuns.length === 0}
                        >
                          <div className="btn-icon" style={{ width: "16px", height: "16px" }}>{iconDownload}</div>
                          <span>Download without filters</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="column-visibility-container" ref={forecastColsRef}>
                  <button id="column-toggle-btn" className="toggle-btn column-toggle-btn"
                    onClick={() => setIsColumnDropdownOpen(!isColumnDropdownOpen)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#f5f5f5" viewBox="0 0 256 256">
                      <path d="M84,52V92H28a4,4,0,0,1-4-4V64A16,16,0,0,1,40,48H80A4,4,0,0,1,84,52Zm16,152a4,4,0,0,0,4,4h48a4,4,0,0,0,4-4V164H100ZM24,168v24a16,16,0,0,0,16,16H80a4,4,0,0,0,4-4V164H28A4,4,0,0,0,24,168Zm0-56v32a4,4,0,0,0,4,4H84V108H28A4,4,0,0,0,24,112ZM152,48H104a4,4,0,0,0-4,4V92h56V52A4,4,0,0,0,152,48Zm76,60H172v40h56a4,4,0,0,0,4-4V112A4,4,0,0,0,228,108ZM100,148h56V108H100ZM216,48H176a4,4,0,0,0-4,4V92h56a4,4,0,0,0,4-4V64A16,16,0,0,0,216,48Zm12,116H172v40a4,4,0,0,0,4,4h40a16,16,0,0,0,16-16V168A4,4,0,0,0,228,164Z"></path>
                    </svg>
                    <span>COLUMNS</span>
                  </button>
                  {isColumnDropdownOpen && (
                    <div id="column-dropdown" className="column-dropdown show">
                      {(forecastMode === "demand" ? forecastColumnOrder : winrateColumns)
                        .filter((col) => col !== "MONTH")
                        .map((colName) => (
                          <div key={colName} className="column-dropdown-item">
                            <span>{colName}</span>
                            <div className="column-visibility-icon"
                              onClick={() => {
                                if (forecastMode === "demand") {
                                  setColumnVisibility((prev) => ({ ...prev, [colName]: !prev[colName] }));
                                } else {
                                  setWinrateColumnVisibility((prev) => ({ ...prev, [colName]: !prev[colName] }));
                                }
                              }}>
                              {(forecastMode === "demand" ? columnVisibility : winrateColumnVisibility)[colName]
                                ? iconVisible : iconHidden}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isForecastLoading ? (
              <div style={{ textAlign: "center", padding: "3rem", color: "#8899aa", fontSize: "1.1rem" }}>
                Loading forecast data...
              </div>
            ) : forecastMode === "demand" ? (
              <>
                {sortedForecastData.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "3rem", color: "#8899aa", fontSize: "1.1rem" }}>
                    No forecast data available
                  </div>
                ) : (
                <>
                <div className="main-table-container forecast-table-container">
                  <table className="main-table">
                    <thead>
                      <tr>
                        {forecastColumnOrder.map((colName) => {
                          if (!columnVisibility[colName]) return null;
                          return (
                            <th key={colName} onClick={() => requestForecastSort(colName)}
                              style={{ cursor: "pointer", userSelect: "none" }}>
                              {colName}{getForecastSortIndicator(colName)}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedForecastData.map((row, i) => (
                        <tr key={i}>
                          {forecastColumnOrder.map((colName) => {
                            if (!columnVisibility[colName]) return null;
                            const def = FORECAST_COLUMN_DEFS[colName];
                            if (!def) return <td key={colName}>-</td>;
                            return (
                              <td key={colName} className={def.className || ""}>
                                {def.render(row, formatNumber, formatCurrency)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="forecast-graphs" key="demand-graphs">
                  <div className="plotly-chart" onClick={(e) => handleChartClick(e, "forecast", "qty", "Forecast QTY")}>
                    <Plot
                      data={getForecastChartConfig("qty", filteredForecastData, false, comparisonData).traces}
                      layout={getForecastChartConfig("qty", filteredForecastData, false, comparisonData).layout}
                      config={getForecastChartConfig("qty", filteredForecastData, false, comparisonData).config}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                  <div className="plotly-chart" onClick={(e) => handleChartClick(e, "forecast", "value", "Forecast VALUE (at AVG COST)")}>
                    <Plot
                      data={getForecastChartConfig("value", filteredForecastData, false, comparisonData).traces}
                      layout={getForecastChartConfig("value", filteredForecastData, false, comparisonData).layout}
                      config={getForecastChartConfig("value", filteredForecastData, false, comparisonData).config}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </div>
                </>
                )}
              </>
            ) : (
              <>
                {sortedWinrateData.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "3rem", color: "#8899aa", fontSize: "1.1rem" }}>
                    No win rate data available
                  </div>
                ) : (
                <>
                <div className="main-table-container forecast-table-container">
                  <table className="main-table">
                    <thead>
                      <tr>
                        {winrateColumns.map((colName) =>
                          showWrCol(colName) && (
                            <th key={colName} onClick={() => requestForecastSort(colName)}
                              style={{ cursor: "pointer", userSelect: "none" }}>
                              {colName}{getForecastSortIndicator(colName)}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedWinrateData.map((row, i) => (
                        <tr key={i}>
                          {showWrCol("MONTH") && <td>{formatPeriod(row.year_week)}</td>}
                          {showWrCol("ACT WR Y-1 RAW") && (
                            <td className={isWinrateCompareActive ? "compare-mode-cell" : ""}>{renderWinrateCell(row, "actual_wr_last_year")}</td>
                          )}
                          {showWrCol("FCST WR (avg) RAW") && (
                            <td className={`fcst-avg${isWinrateCompareActive ? " compare-mode-cell" : ""}`}>{renderWinrateCell(row, "fcst_qty_avg")}</td>
                          )}
                          {showWrCol("ACT WR Y-1 SHRINKED") && (
                            <td className={isWinrateCompareActive ? "compare-mode-cell" : ""}>{renderWinrateCell(row, "actual_wr_last_year_shrinked")}</td>
                          )}
                          {showWrCol("FCST WR (avg) SHRINKED") && (
                            <td className={`fcst-avg${isWinrateCompareActive ? " compare-mode-cell" : ""}`}>{renderWinrateCell(row, "fcst_qty_avg_shrinked")}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="forecast-graphs forecast-graphs--single" key="winrate-graphs">
                  <div className="plotly-chart" onClick={(e) => handleChartClick(e, "winrate", "winrate", "Win Rate Forecast")}>
                    <Plot
                      data={getWinrateChartConfig(filteredWinrateData, false, comparisonData).traces}
                      layout={getWinrateChartConfig(filteredWinrateData, false, comparisonData).layout}
                      config={getWinrateChartConfig(filteredWinrateData, false, comparisonData).config}
                      useResizeHandler={true}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </div>
                </div>
                </>
                )}
              </>
            )}
          </section>
        </main>
      </div>

      {popupChart && (
        <div
          id="image-popup"
          className="image-popup-overlay"
          style={{ display: "flex" }}
          onClick={closePopup}
        >
          <span id="popup-close" className="popup-close" onClick={closePopup}>
            &times;
          </span>
          <div
            id="popup-chart-container"
            className="popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const { type, name, title } = popupChart;
              let chartConfig;
              if (type === "kpi")
                chartConfig = getKpiChartConfig(
                  name,
                  skuKpiHistory,
                  chartConstraints,
                  true,
                  config,
                  unconstrainedSkuKpiHistory,
                );
              else if (type === "forecast")
                chartConfig = getForecastChartConfig(
                  name,
                  filteredForecastData,
                  true,
                  comparisonData
                );
              else if (type === "winrate")
                chartConfig = getWinrateChartConfig(
                  filteredWinrateData,
                  true,
                  comparisonData
                );
              else return null;
              return (
                <Plot
                  data={chartConfig.traces}
                  layout={{
                    ...chartConfig.layout,
                    title: {
                      text: title,
                      font: { size: 20, color: "#333" },
                      x: 0.5,
                    },
                  }}
                  config={chartConfig.config}
                  style={{ width: "100%", height: "100%" }}
                />
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}

export default SkuView;
